import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Skeleton,
  Spinner,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { FiEdit2, FiPlus } from "react-icons/fi";
import { Link as RouterLink, useParams } from "react-router-dom";
import BackButton from "../../_components/BackButton";
import {
  ADMIN_ADD_ORGANISATION_FACILITY_CONTACT,
  ADMIN_CREATE_ORGANISATION_FACILITY,
  ADMIN_GET_ORGANISATION_WALLET_BALANCE,
  ADMIN_GET_ORGANISATION_WALLET_LEDGER_HISTORY,
  ADMIN_UPDATE_ORGANISATION_FACILITY,
  ADMIN_UPDATE_ORGANISATION_FACILITY_CONTACT,
  GET_HOMEOWNER_CLIENT,
  GET_ORGANISATION_CLIENT_PROFILE,
  LIST_HOMEOWNER_CLIENTS,
  LIST_ORGANISATION_CLIENT_PROFILES,
} from "./graphql";

type PageInfo = {
  hasNextPage: boolean;
  endCursor?: string | null;
};

type RelayResponse<T> = {
  pageInfo: PageInfo;
  edges: { cursor: string; node: T }[];
};

type ClientProfile = {
  id: string;
  ownerType: "HOMEOWNER" | "ORGANIZATION";
  createdAt?: string;
  updatedAt?: string;
};

type Address = {
  id: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  formattedAddress?: string | null;
};

type Homeowner = {
  id: string;
  userId: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  createdAt: string;
  updatedAt?: string;
  clientProfile?: ClientProfile | null;
  billingAddress?: Address | null;
  addresses?: Address[] | null;
};

type Organization = {
  id: string;
  name: string;
  referralCode?: string | null;
  countryCode?: string | null;
  createdAt?: string;
};

type LegacyClient = {
  id: string;
  userId: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
};

type FacilityContact = {
  id: string;
  name: string;
  email?: string | null;
  phoneNumber: string;
};

type Facility = {
  id: string;
  name: string;
  address: { id: string; formattedAddress?: string | null };
  contacts: FacilityContact[];
};

type OrganisationClientProfile = {
  id: string;
  contactUserId?: string;
  contactName: string;
  contactEmail: string;
  contactPhoneNumber: string;
  billingName: string;
  createdAt: string;
  updatedAt?: string;
  organization: Organization;
  clientProfile: ClientProfile;
  legacyClient?: LegacyClient | null;
  billingAddress?: Address | null;
  facilities?: Facility[] | null;
};

type LedgerEntry = {
  id: string;
  amount: number;
  type: "CREDIT" | "DEBIT";
  transactionType: "DEPOSIT" | "SERVICE_PAYMENT" | "REFUND";
  description?: string | null;
  createdAt: string;
  initiatedBy?: {
    id: string;
    name?: string | null;
    email?: string | null;
  } | null;
};

const PAGE_SIZE = 20;

const shortId = (value?: string | null) => value?.slice(0, 8) || "N/A";

const formatDate = (value?: string | null) => {
  if (!value) return "N/A";

  return new Date(value).toLocaleString();
};

const formatName = (firstName?: string | null, lastName?: string | null) => {
  const fullName = `${firstName || ""} ${lastName || ""}`.trim();
  return fullName || "N/A";
};

const formatNaira = (amount: number) =>
  new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 2,
  }).format(amount);

const formatInitiator = (initiatedBy: LedgerEntry["initiatedBy"]) => {
  if (!initiatedBy) return "—";
  return initiatedBy.name || initiatedBy.email || initiatedBy.id;
};

const formatAddress = (address?: Address | null) => {
  if (!address) return "N/A";

  return (
    address.formattedAddress ||
    [address.address, address.city, address.state].filter(Boolean).join(", ") ||
    "N/A"
  );
};

const PageHeader = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => (
  <Flex
    align={{ base: "flex-start", md: "center" }}
    direction={{ base: "column", md: "row" }}
    justify="space-between"
    gap={4}
    mb={6}
  >
    <Box>
      <Heading as="h1" size="lg" color="primary.500" letterSpacing={0}>
        {title}
      </Heading>
      <Text color="primary.300" mt={1} mb={0}>
        {description}
      </Text>
    </Box>
  </Flex>
);

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Box bg="white" border="1px solid" borderColor="primary.150" borderRadius="md" p={5}>
    <Heading as="h2" size="sm" color="primary.500" letterSpacing={0} mb={4}>
      {title}
    </Heading>
    <Stack spacing={3}>{children}</Stack>
  </Box>
);

const FieldRow = ({
  label,
  value,
}: {
  label: string;
  value?: React.ReactNode;
}) => (
  <Flex gap={4} justify="space-between" align="flex-start">
    <Text color="primary.300" fontSize="sm" minW="120px" mb={0}>
      {label}
    </Text>
    <Text color="primary.500" fontWeight="semibold" textAlign="right" mb={0}>
      {value || "N/A"}
    </Text>
  </Flex>
);

const LoadingState = () => (
  <Flex minH="180px" align="center" justify="center">
    <Spinner color="brand.500" />
  </Flex>
);

const ErrorState = ({ message = "Unable to load client profile data." }) => (
  <Alert status="error" borderRadius="md">
    <AlertIcon />
    <AlertDescription>{message}</AlertDescription>
  </Alert>
);

const EmptyState = ({ message }: { message: string }) => (
  <Flex minH="160px" align="center" justify="center" bg="primary.100" borderRadius="md">
    <Text color="primary.300" fontWeight="semibold" mb={0}>
      {message}
    </Text>
  </Flex>
);

const LoadMoreButton = ({
  loading,
  hasMore,
  onClick,
}: {
  loading: boolean;
  hasMore?: boolean;
  onClick: () => void;
}) => (
  <Flex justify="center" mt={4}>
    <Button
      variant="outline"
      colorScheme="brand"
      isLoading={loading}
      isDisabled={!hasMore}
      onClick={onClick}
    >
      {hasMore ? "Load more" : "All loaded"}
    </Button>
  </Flex>
);

export const HomeownerClientsList = () => {
  const { data, loading, error, fetchMore } = useQuery<{
    listHomeowners: RelayResponse<Homeowner>;
  }>(LIST_HOMEOWNER_CLIENTS, {
    variables: { first: PAGE_SIZE, after: null },
    notifyOnNetworkStatusChange: true,
  });

  const homeowners = data?.listHomeowners.edges.map((edge) => edge.node) || [];

  if (error) return <ErrorState message={error.message} />;

  return (
    <Box>
      <PageHeader
        title="Homeowners"
        description="Homeowner client profiles registered on LaborHack."
      />
      {loading && !data ? (
        <LoadingState />
      ) : homeowners.length === 0 ? (
        <EmptyState message="No homeowner client profiles found." />
      ) : (
        <>
          <TableContainer bg="white" border="1px solid" borderColor="primary.150" borderRadius="md">
            <Table variant="simple">
              <Thead bg="primary.100">
                <Tr>
                  <Th>ID</Th>
                  <Th>Name</Th>
                  <Th>Email</Th>
                  <Th>Phone Number</Th>
                  <Th>Client Profile</Th>
                  <Th>Date Created</Th>
                  <Th></Th>
                </Tr>
              </Thead>
              <Tbody>
                {homeowners.map((homeowner) => (
                  <Tr key={homeowner.id}>
                    <Td>{shortId(homeowner.id)}</Td>
                    <Td fontWeight="semibold">
                      {formatName(homeowner.firstName, homeowner.lastName)}
                    </Td>
                    <Td>{homeowner.email || "N/A"}</Td>
                    <Td>{homeowner.phoneNumber || "N/A"}</Td>
                    <Td>{shortId(homeowner.clientProfile?.id)}</Td>
                    <Td>{formatDate(homeowner.createdAt)}</Td>
                    <Td>
                      <Button
                        as={RouterLink}
                        to={`/clients/homeowners/${homeowner.id}`}
                        size="sm"
                        variant="outline"
                        aria-label="View homeowner"
                      >
                        View
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
          <LoadMoreButton
            loading={loading}
            hasMore={data?.listHomeowners.pageInfo.hasNextPage}
            onClick={() =>
              fetchMore({
                variables: {
                  first: PAGE_SIZE,
                  after: data?.listHomeowners.pageInfo.endCursor,
                },
              })
            }
          />
        </>
      )}
    </Box>
  );
};

export const HomeownerClientDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { data, loading, error } = useQuery<{ homeowner: Homeowner }>(
    GET_HOMEOWNER_CLIENT,
    { variables: { id } }
  );

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error.message} />;
  if (!data?.homeowner) return <EmptyState message="Homeowner client profile not found." />;

  const homeowner = data.homeowner;
  const clientProfile = homeowner.clientProfile;

  return (
    <Stack spacing={5}>
      <BackButton />
      <Box>
        <Heading as="h1" size="lg" color="primary.500" letterSpacing={0}>
          {formatName(homeowner.firstName, homeowner.lastName)}
        </Heading>
        <Flex gap={2} align="center" mt={2}>
          <Badge colorScheme="green">Homeowner</Badge>
          <Text color="primary.300" mb={0}>
            {homeowner.id}
          </Text>
        </Flex>
      </Box>
      <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={5}>
        <Section title="Profile Summary">
          <FieldRow label="Homeowner ID" value={homeowner.id} />
          <FieldRow label="User ID" value={homeowner.userId} />
          <FieldRow label="Created" value={formatDate(homeowner.createdAt)} />
          <FieldRow label="Updated" value={formatDate(homeowner.updatedAt)} />
        </Section>
        <Section title="Personal Information">
          <FieldRow label="Name" value={formatName(homeowner.firstName, homeowner.lastName)} />
          <FieldRow label="Email" value={homeowner.email || "N/A"} />
          <FieldRow label="Phone" value={homeowner.phoneNumber || "N/A"} />
        </Section>
        <Section title="Client Profile">
          <FieldRow label="Profile ID" value={clientProfile?.id || "N/A"} />
          <FieldRow label="Owner Type" value={clientProfile?.ownerType || "N/A"} />
          <FieldRow label="Created" value={formatDate(clientProfile?.createdAt)} />
          <FieldRow label="Updated" value={formatDate(clientProfile?.updatedAt)} />
        </Section>
        <Section title="Billing And Addresses">
          <FieldRow label="Billing Address" value={formatAddress(homeowner.billingAddress)} />
          <Divider />
          <FieldRow label="Saved Addresses" value={homeowner.addresses?.length || 0} />
        </Section>
      </SimpleGrid>
    </Stack>
  );
};

export const OrganisationClientsList = () => {
  const { data, loading, error, fetchMore } = useQuery<{
    organizationClientProfiles: RelayResponse<OrganisationClientProfile>;
  }>(LIST_ORGANISATION_CLIENT_PROFILES, {
    variables: { first: PAGE_SIZE, after: null },
    notifyOnNetworkStatusChange: true,
  });

  const profiles = data?.organizationClientProfiles.edges.map((edge) => edge.node) || [];

  if (error) return <ErrorState message={error.message} />;

  return (
    <Box>
      <PageHeader
        title="Organisations"
        description="Organisation client profiles that can request services."
      />
      {loading && !data ? (
        <LoadingState />
      ) : profiles.length === 0 ? (
        <EmptyState message="No organisation client profiles found." />
      ) : (
        <>
          <TableContainer bg="white" border="1px solid" borderColor="primary.150" borderRadius="md">
            <Table variant="simple">
              <Thead bg="primary.100">
                <Tr>
                  <Th>Organisation</Th>
                  <Th>Organisation ID</Th>
                  <Th>Contact</Th>
                  <Th>Email</Th>
                  <Th>Phone</Th>
                  <Th>Billing Name</Th>
                  <Th>Client Profile</Th>
                  <Th>Date Created</Th>
                  <Th></Th>
                </Tr>
              </Thead>
              <Tbody>
                {profiles.map((profile) => (
                  <Tr key={profile.id}>
                    <Td fontWeight="semibold">{profile.organization.name}</Td>
                    <Td>{shortId(profile.organization.id)}</Td>
                    <Td>{profile.contactName}</Td>
                    <Td>{profile.contactEmail}</Td>
                    <Td>{profile.contactPhoneNumber}</Td>
                    <Td>{profile.billingName}</Td>
                    <Td>{shortId(profile.clientProfile.id)}</Td>
                    <Td>{formatDate(profile.createdAt)}</Td>
                    <Td>
                      <Button
                        as={RouterLink}
                        to={`/clients/organisations/${profile.id}`}
                        size="sm"
                        variant="outline"
                        aria-label="View organisation"
                      >
                        View
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
          <LoadMoreButton
            loading={loading}
            hasMore={data?.organizationClientProfiles.pageInfo.hasNextPage}
            onClick={() =>
              fetchMore({
                variables: {
                  first: PAGE_SIZE,
                  after: data?.organizationClientProfiles.pageInfo.endCursor,
                },
              })
            }
          />
        </>
      )}
    </Box>
  );
};

const WalletCard: React.FC<{ organizationId: string }> = ({ organizationId }) => {
  const { data, loading, error } = useQuery<{
    adminOrganizationWalletBalance: number;
  }>(ADMIN_GET_ORGANISATION_WALLET_BALANCE, {
    variables: { organizationId },
  });

  return (
    <Section title="Wallet">
      {loading ? (
        <Skeleton height="48px" />
      ) : error ? (
        <Text color="red.500" mb={0}>
          {error.message}
        </Text>
      ) : (
        <Stat>
          <StatLabel color="primary.300">Current balance</StatLabel>
          <StatNumber color="primary.500">
            {formatNaira(data?.adminOrganizationWalletBalance ?? 0)}
          </StatNumber>
        </Stat>
      )}
    </Section>
  );
};

const FacilityDialog: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  facility?: Facility;
}> = ({ isOpen, onClose, organizationId, facility }) => {
  const isEdit = !!facility;
  const [name, setName] = useState(facility?.name ?? "");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");

  const [createFacility, { loading: creating }] = useMutation(
    ADMIN_CREATE_ORGANISATION_FACILITY,
    { refetchQueries: [GET_ORGANISATION_CLIENT_PROFILE] }
  );
  const [updateFacility, { loading: updating }] = useMutation(
    ADMIN_UPDATE_ORGANISATION_FACILITY,
    { refetchQueries: [GET_ORGANISATION_CLIENT_PROFILE] }
  );

  const submit = async () => {
    if (isEdit) {
      const hasAddress = !!(address || city || state);
      await updateFacility({
        variables: {
          facilityId: facility!.id,
          payload: {
            name: name || undefined,
            address: hasAddress ? { address, city, state } : undefined,
          },
        },
      });
    } else {
      await createFacility({
        variables: {
          organizationId,
          payload: { name, address: { address, city, state } },
        },
      });
    }
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{isEdit ? "Edit facility" : "Add facility"}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={3}>
            <FormControl isRequired={!isEdit}>
              <FormLabel>Name</FormLabel>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </FormControl>
            <FormControl isRequired={!isEdit}>
              <FormLabel>Address line</FormLabel>
              <Input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </FormControl>
            <FormControl isRequired={!isEdit}>
              <FormLabel>City</FormLabel>
              <Input value={city} onChange={(e) => setCity(e.target.value)} />
            </FormControl>
            <FormControl isRequired={!isEdit}>
              <FormLabel>State</FormLabel>
              <Input value={state} onChange={(e) => setState(e.target.value)} />
            </FormControl>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button mr={2} variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="brand"
            onClick={submit}
            isLoading={creating || updating}
          >
            {isEdit ? "Save" : "Create"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const FacilityContactDialog: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  facilityId: string;
  contact?: FacilityContact;
}> = ({ isOpen, onClose, facilityId, contact }) => {
  const isEdit = !!contact;
  const [name, setName] = useState(contact?.name ?? "");
  const [email, setEmail] = useState(contact?.email ?? "");
  const [phoneNumber, setPhoneNumber] = useState(contact?.phoneNumber ?? "");

  const [addContact, { loading: adding }] = useMutation(
    ADMIN_ADD_ORGANISATION_FACILITY_CONTACT,
    { refetchQueries: [GET_ORGANISATION_CLIENT_PROFILE] }
  );
  const [updateContact, { loading: updating }] = useMutation(
    ADMIN_UPDATE_ORGANISATION_FACILITY_CONTACT,
    { refetchQueries: [GET_ORGANISATION_CLIENT_PROFILE] }
  );

  const submit = async () => {
    if (isEdit) {
      await updateContact({
        variables: {
          contactId: contact!.id,
          payload: {
            name: name || undefined,
            email: email || undefined,
            phoneNumber: phoneNumber || undefined,
          },
        },
      });
    } else {
      await addContact({
        variables: {
          payload: { facilityId, name, email: email || undefined, phoneNumber },
        },
      });
    }
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{isEdit ? "Edit contact" : "Add contact"}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={3}>
            <FormControl isRequired={!isEdit}>
              <FormLabel>Name</FormLabel>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </FormControl>
            <FormControl>
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </FormControl>
            <FormControl isRequired={!isEdit}>
              <FormLabel>Phone number</FormLabel>
              <Input
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
              />
            </FormControl>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button mr={2} variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="brand"
            onClick={submit}
            isLoading={adding || updating}
          >
            {isEdit ? "Save" : "Add"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const FacilityRow: React.FC<{ facility: Facility }> = ({ facility }) => {
  const editDialog = useDisclosure();
  const addContactDialog = useDisclosure();
  const [editingContact, setEditingContact] = useState<FacilityContact | null>(
    null
  );

  return (
    <Box borderWidth="1px" borderColor="primary.150" borderRadius="md" p={4}>
      <Flex justify="space-between" align="center" gap={3}>
        <Box>
          <Text fontWeight="semibold" mb={0}>
            {facility.name}
          </Text>
          <Text color="primary.300" fontSize="sm" mb={0}>
            {facility.address?.formattedAddress || "No address"}
          </Text>
        </Box>
        <IconButton
          aria-label="Edit facility"
          icon={<FiEdit2 />}
          size="sm"
          variant="ghost"
          onClick={editDialog.onOpen}
        />
      </Flex>

      <Box mt={3}>
        <Flex justify="space-between" align="center" mb={2}>
          <Text fontWeight="medium" mb={0}>
            Contacts
          </Text>
          <Button
            size="xs"
            leftIcon={<FiPlus />}
            onClick={addContactDialog.onOpen}
          >
            Add contact
          </Button>
        </Flex>
        {facility.contacts.length === 0 ? (
          <Text color="primary.300" fontSize="sm" mb={0}>
            No contacts yet.
          </Text>
        ) : (
          <Stack spacing={2}>
            {facility.contacts.map((contact) => (
              <Flex
                key={contact.id}
                justify="space-between"
                align="center"
                borderWidth="1px"
                borderColor="primary.150"
                borderRadius="sm"
                px={3}
                py={2}
              >
                <Box>
                  <Text fontWeight="medium" mb={0}>
                    {contact.name}
                  </Text>
                  <Text fontSize="sm" color="primary.400" mb={0}>
                    {contact.email || "—"} · {contact.phoneNumber}
                  </Text>
                </Box>
                <IconButton
                  aria-label="Edit contact"
                  icon={<FiEdit2 />}
                  size="xs"
                  variant="ghost"
                  onClick={() => setEditingContact(contact)}
                />
              </Flex>
            ))}
          </Stack>
        )}
      </Box>

      <FacilityDialog
        isOpen={editDialog.isOpen}
        onClose={editDialog.onClose}
        organizationId=""
        facility={facility}
      />
      <FacilityContactDialog
        isOpen={addContactDialog.isOpen}
        onClose={addContactDialog.onClose}
        facilityId={facility.id}
      />
      {editingContact && (
        <FacilityContactDialog
          isOpen
          onClose={() => setEditingContact(null)}
          facilityId={facility.id}
          contact={editingContact}
        />
      )}
    </Box>
  );
};

const FacilitiesSection: React.FC<{
  organizationId: string;
  facilities: Facility[];
}> = ({ organizationId, facilities }) => {
  const addDialog = useDisclosure();

  return (
    <Box bg="white" border="1px solid" borderColor="primary.150" borderRadius="md" p={5}>
      <Flex justify="space-between" align="center" mb={4}>
        <Heading as="h2" size="sm" color="primary.500" letterSpacing={0} mb={0}>
          Facilities
        </Heading>
        <Button size="sm" leftIcon={<FiPlus />} onClick={addDialog.onOpen}>
          Add facility
        </Button>
      </Flex>
      {facilities.length === 0 ? (
        <Text color="primary.300" mb={0}>
          No facilities added yet.
        </Text>
      ) : (
        <Stack spacing={3}>
          {facilities.map((facility) => (
            <FacilityRow key={facility.id} facility={facility} />
          ))}
        </Stack>
      )}
      <FacilityDialog
        isOpen={addDialog.isOpen}
        onClose={addDialog.onClose}
        organizationId={organizationId}
      />
    </Box>
  );
};

const LEDGER_PAGE_SIZE = 20;

type LedgerConnection = {
  totalCount: number;
  pageInfo: { hasNextPage: boolean; endCursor: string | null };
  edges: { cursor: string; node: LedgerEntry }[];
};

const DrawdownSection: React.FC<{ organizationId: string }> = ({
  organizationId,
}) => {
  const { data, loading, error, fetchMore } = useQuery<{
    adminOrganizationWalletLedgerHistory: LedgerConnection;
  }>(ADMIN_GET_ORGANISATION_WALLET_LEDGER_HISTORY, {
    variables: { organizationId, first: LEDGER_PAGE_SIZE, after: null },
    notifyOnNetworkStatusChange: true,
  });

  if (loading && !data) {
    return (
      <Section title="Wallet activity">
        <LoadingState />
      </Section>
    );
  }

  if (error) {
    return (
      <Section title="Wallet activity">
        <ErrorState message={error.message} />
      </Section>
    );
  }

  const conn = data?.adminOrganizationWalletLedgerHistory;
  const edges = conn?.edges ?? [];

  if (edges.length === 0) {
    return (
      <Section title="Wallet activity">
        <Text color="primary.300" mb={0}>
          No wallet activity yet.
        </Text>
      </Section>
    );
  }

  return (
    <Box bg="white" border="1px solid" borderColor="primary.150" borderRadius="md" p={5}>
      <Heading as="h2" size="sm" color="primary.500" letterSpacing={0} mb={4}>
        Wallet activity
      </Heading>
      <TableContainer>
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>Date</Th>
              <Th>Type</Th>
              <Th>Transaction</Th>
              <Th isNumeric>Amount</Th>
              <Th>Description</Th>
              <Th>Initiated by</Th>
            </Tr>
          </Thead>
          <Tbody>
            {edges.map(({ node }) => (
              <Tr key={node.id}>
                <Td>{formatDate(node.createdAt)}</Td>
                <Td>
                  <Badge
                    colorScheme={node.type === "CREDIT" ? "green" : "red"}
                  >
                    {node.type}
                  </Badge>
                </Td>
                <Td>{node.transactionType}</Td>
                <Td isNumeric>{formatNaira(node.amount)}</Td>
                <Td>{node.description || "—"}</Td>
                <Td>{formatInitiator(node.initiatedBy)}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
      <LoadMoreButton
        loading={loading}
        hasMore={conn?.pageInfo.hasNextPage}
        onClick={() =>
          fetchMore({
            variables: {
              organizationId,
              first: LEDGER_PAGE_SIZE,
              after: conn?.pageInfo.endCursor,
            },
          })
        }
      />
    </Box>
  );
};

export const OrganisationClientDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { data, loading, error } = useQuery<{
    organizationClientProfile: OrganisationClientProfile | null;
  }>(GET_ORGANISATION_CLIENT_PROFILE, { variables: { id } });

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error.message} />;
  if (!data?.organizationClientProfile) {
    return <EmptyState message="Organisation client profile not found." />;
  }

  const profile = data.organizationClientProfile;

  return (
    <Stack spacing={5}>
      <BackButton />
      <Box>
        <Heading as="h1" size="lg" color="primary.500" letterSpacing={0}>
          {profile.organization.name}
        </Heading>
        <Flex gap={2} align="center" mt={2}>
          <Badge colorScheme="blue">Organisation</Badge>
          <Text color="primary.300" mb={0}>
            {profile.id}
          </Text>
        </Flex>
      </Box>
      <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={5}>
        <Section title="Organisation Information">
          <FieldRow label="Organisation ID" value={profile.organization.id} />
          <FieldRow label="Name" value={profile.organization.name} />
          <FieldRow label="Referral Code" value={profile.organization.referralCode || "N/A"} />
          <FieldRow label="Country" value={profile.organization.countryCode || "N/A"} />
          <FieldRow label="Created" value={formatDate(profile.organization.createdAt)} />
        </Section>
        <Section title="Client Profile">
          <FieldRow label="Metadata ID" value={profile.id} />
          <FieldRow label="Profile ID" value={profile.clientProfile.id} />
          <FieldRow label="Owner Type" value={profile.clientProfile.ownerType} />
          <FieldRow label="Created" value={formatDate(profile.clientProfile.createdAt)} />
          <FieldRow label="Updated" value={formatDate(profile.clientProfile.updatedAt)} />
        </Section>
        <Section title="Contact Person">
          <FieldRow label="Name" value={profile.contactName} />
          <FieldRow label="Email" value={profile.contactEmail} />
          <FieldRow label="Phone" value={profile.contactPhoneNumber} />
          <FieldRow label="User ID" value={profile.contactUserId || "N/A"} />
        </Section>
        <Section title="Billing">
          <FieldRow label="Billing Name" value={profile.billingName} />
          <FieldRow label="Billing Address" value={formatAddress(profile.billingAddress)} />
        </Section>
        <Section title="Legacy Client Compatibility">
          <FieldRow label="Client ID" value={profile.legacyClient?.id || "N/A"} />
          <FieldRow label="User ID" value={profile.legacyClient?.userId || "N/A"} />
          <FieldRow
            label="Name"
            value={formatName(profile.legacyClient?.firstName, profile.legacyClient?.lastName)}
          />
          <FieldRow label="Email" value={profile.legacyClient?.email || "N/A"} />
          <FieldRow label="Phone" value={profile.legacyClient?.phoneNumber || "N/A"} />
        </Section>
      </SimpleGrid>
      <WalletCard organizationId={profile.organization.id} />
      <FacilitiesSection
        organizationId={profile.organization.id}
        facilities={profile.facilities ?? []}
      />
      <DrawdownSection organizationId={profile.organization.id} />
    </Stack>
  );
};
